/*
 * Modal plumbing shared by every overlay (mini cart, quick view, palette,
 * mobile drawer, lightbox). While `open` is true: Tab cycles inside `root`,
 * Escape calls `onClose`, the page behind stops scrolling (nesting-safe),
 * and on close focus returns to the element that opened the overlay.
 * Owns no markup and no open/close state.
 */
import { nextTick, onBeforeUnmount, watch, type Ref } from 'vue';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Quick view → lightbox: the inner overlay must not unlock the page early.
let scrollLocks = 0;
let savedOverflow = '';

function lockScroll() {
  if (scrollLocks++ === 0) {
    savedOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
}

function unlockScroll() {
  if (scrollLocks > 0 && --scrollLocks === 0) document.body.style.overflow = savedOverflow;
}

export interface ModalFocusOptions {
  /** Element focused on open; default is the first focusable inside `root`. */
  initial?: Ref<HTMLElement | null>;
}

export function useModalFocus(root: Ref<HTMLElement | null>, open: Ref<boolean>, onClose: () => void, options: ModalFocusOptions = {}) {
  let opener: HTMLElement | null = null;
  let active = false;

  function focusable(): HTMLElement[] {
    if (!root.value) return [];
    return [...root.value.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.getClientRects().length > 0);
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = focusable();
    if (items.length === 0) {
      e.preventDefault();
      root.value?.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const inside = root.value?.contains(document.activeElement) ?? false;
    if (e.shiftKey && (!inside || document.activeElement === first)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
      e.preventDefault();
      first.focus();
    }
  }

  async function activate() {
    if (active) return;
    active = true;
    opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    lockScroll();
    document.addEventListener('keydown', onKeydown);
    await nextTick();
    (options.initial?.value ?? focusable()[0] ?? root.value)?.focus();
  }

  function deactivate() {
    if (!active) return;
    active = false;
    document.removeEventListener('keydown', onKeydown);
    unlockScroll();
    if (opener?.isConnected) opener.focus();
    opener = null;
  }

  watch(open, (isOpen) => (isOpen ? void activate() : deactivate()), { immediate: true });
  onBeforeUnmount(deactivate);
}
